import { useMemo, useState } from "react";
import { Search, UserRound, Star, ChevronDown, ChevronRight, Info, X, Filter, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useApi } from "@/modules/collector_v2/hooks/useApi";
import { formatNumber } from "@/modules/collector_v2/lib/formatters";
import { formatEpochDate } from "@/modules/collector_v2/lib/replayFormatters";
import { PageHeader } from "@/modules/collector_v2/components/common/PageHeader";
import { LoadingState } from "@/modules/collector_v2/components/common/LoadingState";
import { ErrorState } from "@/modules/collector_v2/components/common/ErrorState";
import { EmptyState } from "@/modules/collector_v2/components/common/EmptyState";
import { PlayerProfileDrawer } from "@/modules/collector_v2/components/profile/PlayerProfileDrawer";

// ---------------------------------------------------------------------------
// Player type → estilo del badge.
// Inspirado en la nomenclatura clásica de PT4/H2N adaptada a un solo glance.
// ---------------------------------------------------------------------------
const PLAYER_TYPE_STYLES = {
  MANIAC: "bg-rose-100 text-rose-800 border-rose-200",
  FISH:   "bg-amber-100 text-amber-800 border-amber-200",
  LAG:    "bg-violet-100 text-violet-800 border-violet-200",
  TAG:    "bg-emerald-100 text-emerald-800 border-emerald-200",
  NIT:    "bg-sky-100 text-sky-800 border-sky-200",
  REG:    "bg-slate-100 text-slate-700 border-slate-200",
};

function PlayerTypeBadge({ type }) {
  if (!type) return <span className="text-[11px] text-muted-foreground">—</span>;
  const cls = PLAYER_TYPE_STYLES[type] || PLAYER_TYPE_STYLES.REG;
  return (
    <Badge
      className={`text-[10px] font-bold border ${cls}`}
      data-testid={`player-type-badge-${type}`}
    >
      {type}
    </Badge>
  );
}

// Formatea ratio (0..1) como porcentaje. Devuelve "N/A" si null/undefined
// (típicamente jugadores que solo juegan bomb pots → VPIP no aplica).
function fmtPct(v) {
  if (v == null) return <span className="text-slate-300 font-medium">N/A</span>;
  return `${(Number(v) * 100).toFixed(1)}%`;
}

// Color para profit BB (verde positivo, rojo negativo, gris neutral).
function profitColor(v) {
  if (v == null) return "text-muted-foreground";
  if (v > 0.01) return "text-emerald-600 font-semibold";
  if (v < -0.01) return "text-rose-600 font-semibold";
  return "text-slate-600";
}

// ---------------------------------------------------------------------------
// Definiciones de stats: para popovers explicativos al clickear el header.
// ---------------------------------------------------------------------------
const STAT_DEFINITIONS = {
  VPIP: {
    title: "VPIP — Voluntary Put $ In Pot",
    formula: "VPIP = (calls + bets + raises preflop) / (manos NO bomb-pot)",
    description:
      "Porcentaje de manos donde el jugador puso fichas voluntariamente preflop (no cuenta blinds posteados ni bomb pots).",
    interpretation: [
      ["< 15%", "NIT — muy ajustado, solo juega manos premium"],
      ["18-25%", "TAG — Tight-Aggressive, rango sólido"],
      ["25-35%", "LAG — Loose-Aggressive (si AGG alto)"],
      ["35-50%", "FISH — abre demasiado, explotable"],
      ["> 50%", "MANIAC — juega casi todo, hyper-explotable"],
      ["N/A", "Solo juega bomb pots → VPIP no aplica"],
    ],
    note: "💡 En bomb pots todos pagan BB forzado, por eso esas manos se excluyen del cálculo.",
  },
  PFR: {
    title: "PFR — Pre-Flop Raise",
    formula: "PFR = (raises preflop) / (manos NO bomb-pot)",
    description:
      "Porcentaje de manos donde el jugador levantó (raise) preflop. Indicador clave de agresión inicial. Excluye bomb pots.",
    interpretation: [
      ["VPIP-PFR < 5", "Agresivo coherente (3bet/open frecuente)"],
      ["VPIP-PFR 5-10", "Calling Station moderado"],
      ["VPIP-PFR > 15", "Calling Station puro (demasiado limp/call)"],
      ["N/A", "Solo juega bomb pots → PFR no aplica"],
    ],
  },
  AGG: {
    title: "AGG — Aggression Rate",
    formula: "AGG = (bets + raises) / (calls + bets + raises) en TODAS las calles",
    description:
      "Cuán agresivo es post-flop. Excluye folds y acciones automáticas (SYSTEM_FOLD, BOMB_POT_BB, blinds).",
    interpretation: [
      ["< 30%", "Pasivo — call/check predominante"],
      ["30-50%", "Balanceado"],
      ["50-70%", "Agresivo — buena estructura ofensiva"],
      ["> 70%", "Hyper-agresivo — bluffea demasiado"],
    ],
  },
  "AVG BB": {
    title: "Avg BB — Profit promedio por mano",
    formula: "Avg BB = Σ(profit_bb por mano) / total_manos",
    description:
      "Ganancia/pérdida promedio en BBs por cada mano jugada. Métrica de winrate. Profits anómalos (>1000 BB) se descartan automáticamente.",
    interpretation: [
      ["+5 BB/mano y más", "Top winner (insostenible long term, muestra chica)"],
      ["+0.5 a +3 BB/mano", "Winner sólido"],
      ["-2 a +0.5 BB/mano", "Breakeven / micro-winner"],
      ["< -3 BB/mano", "Loser claro"],
    ],
    note: "💡 Se necesitan al menos 500 manos para considerarlo sólido.",
  },
};

function StatHelpPopover({ stat, children }) {
  const def = STAT_DEFINITIONS[stat];
  if (!def) return children;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 hover:text-cyan-700 cursor-help transition-colors"
          data-testid={`stat-help-trigger-${stat}`}
        >
          {children}
          <Info className="h-3 w-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[380px] p-4"
        align="center"
        data-testid={`stat-help-popover-${stat}`}
      >
        <div className="space-y-3">
          <div>
            <h4 className="font-bold text-sm">{def.title}</h4>
            <p className="text-xs text-muted-foreground mt-1">{def.description}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded p-2 font-mono text-[11px] text-slate-700">
            {def.formula}
          </div>
          <div>
            <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
              Interpretación
            </h5>
            <table className="w-full text-xs">
              <tbody>
                {def.interpretation.map(([range, desc], i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="py-1 font-mono font-semibold text-cyan-700 pr-2 whitespace-nowrap align-top">
                      {range}
                    </td>
                    <td className="py-1 text-slate-600">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {def.note && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              {def.note}
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Indicador visual de calidad de muestra estadística.
// 1=poco fiable, 4=muy fiable. Ver profiling_pipeline.py.
function SampleQualityBadge({ quality, hands }) {
  if (!quality) return null;
  const stars = "★".repeat(quality) + "☆".repeat(4 - quality);
  const tones = {
    1: "text-slate-300",
    2: "text-amber-500",
    3: "text-emerald-500",
    4: "text-emerald-600",
  };
  const labels = {
    1: "Muestra insuficiente (<30 manos)",
    2: "Orientativa (30-99 manos)",
    3: "Confiable (100-499 manos)",
    4: "Muy confiable (500+ manos)",
  };
  return (
    <span
      className={`text-[10px] font-bold tracking-tighter tabular-nums ${tones[quality]}`}
      data-testid={`sample-quality-${quality}`}
      title={`${labels[quality]} · ${hands || 0} manos`}
    >
      {stars}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Dropdown reutilizable para filtrar por una columna (Club / Unión).
// Pinta el label + ícono de filtro junto al header. Al abrir, muestra un
// `Command` con búsqueda fuzzy y la lista de opciones disponibles según los
// jugadores cargados.
// ---------------------------------------------------------------------------
function ColumnFilterDropdown({ label, options, value, onChange, testidPrefix, placeholder }) {
  const isActive = !!value?.id;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 -mx-1 px-1 py-0.5 rounded transition-colors ${
            isActive ? "text-cyan-700 bg-cyan-50" : "hover:text-cyan-700 hover:bg-slate-50"
          }`}
          data-testid={`${testidPrefix}-header-trigger`}
          title={`Filtrar por ${label}`}
        >
          <span>{label}</span>
          <Filter className={`h-3 w-3 ${isActive ? "opacity-100" : "opacity-50"}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder={placeholder || `Buscar ${label.toLowerCase()}...`} />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>Sin coincidencias.</CommandEmpty>
            <CommandGroup>
              {isActive && (
                <CommandItem
                  onSelect={() => onChange(null)}
                  className="text-rose-600 font-medium"
                  data-testid={`${testidPrefix}-clear-option`}
                >
                  <X className="h-3.5 w-3.5 mr-2" />
                  Quitar filtro
                </CommandItem>
              )}
              {options.map((opt) => {
                const selected = String(value?.id || "") === String(opt.id);
                return (
                  <CommandItem
                    key={opt.id}
                    value={`${opt.name || ""} ${opt.id}`}
                    onSelect={() => onChange({ id: opt.id, name: opt.name || null })}
                    className={selected ? "bg-cyan-50 text-cyan-800" : ""}
                    data-testid={`${testidPrefix}-option-${opt.id}`}
                  >
                    <div className="flex flex-col leading-tight min-w-0">
                      <span className="text-sm truncate">
                        {opt.name || <span className="italic text-muted-foreground">sin nombre</span>}
                      </span>
                      <span className="text-[10px] text-muted-foreground font-mono tabular-nums">
                        ID {opt.id} · {opt.count} {opt.count === 1 ? "jugador" : "jugadores"}
                      </span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function PlayersTable({ players, onOpenProfile, statsExpanded, onToggleStats, onSelectClub, onSelectUnion, clubOptions, unionOptions, clubFilter, unionFilter }) {
  // Header del toggle: ChevronDown abierto / ChevronRight cerrado.
  const ToggleIcon = statsExpanded ? ChevronDown : ChevronRight;

  return (
    <Card data-testid="players-table-card">
      <CardContent className="pt-6">
        <Table data-testid="players-table" aria-label="Tabla de jugadores">
          <TableHeader>
            <TableRow>
              <TableHead>Jugador</TableHead>
              <TableHead className="w-[180px]">
                <ColumnFilterDropdown
                  label="Club"
                  options={clubOptions}
                  value={clubFilter}
                  onChange={onSelectClub}
                  testidPrefix="players-club-filter"
                />
              </TableHead>
              <TableHead className="w-[90px]">
                <ColumnFilterDropdown
                  label="Unión"
                  options={unionOptions}
                  value={unionFilter}
                  onChange={onSelectUnion}
                  testidPrefix="players-union-filter"
                />
              </TableHead>
              <TableHead className="w-[100px] text-right">Manos</TableHead>
              <TableHead className="w-[80px]">Tipo</TableHead>
              {/* Toggle column header: clickable, controla visibilidad del grupo de stats */}
              <TableHead
                className="w-[44px] text-center bg-slate-50 cursor-pointer hover:bg-slate-100 select-none transition-colors"
                onClick={onToggleStats}
                data-testid="stats-toggle-header"
                title={statsExpanded ? "Ocultar estadísticas" : "Mostrar estadísticas"}
              >
                <ToggleIcon className="h-4 w-4 mx-auto text-slate-600" />
              </TableHead>
              {statsExpanded && (
                <>
                  <TableHead className="w-[80px] text-right">
                    <StatHelpPopover stat="VPIP">VPIP</StatHelpPopover>
                  </TableHead>
                  <TableHead className="w-[80px] text-right">
                    <StatHelpPopover stat="PFR">PFR</StatHelpPopover>
                  </TableHead>
                  <TableHead className="w-[80px] text-right">
                    <StatHelpPopover stat="AGG">AGG</StatHelpPopover>
                  </TableHead>
                  <TableHead className="w-[100px] text-right">
                    <StatHelpPopover stat="AVG BB">Avg BB</StatHelpPopover>
                  </TableHead>
                </>
              )}
              <TableHead className="w-[160px]">Última actividad</TableHead>
              <TableHead className="w-[100px] text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {players.map((player) => {
              const stats = player.stats;
              const showCulture = player.show_culture;
              return (
                <TableRow key={player.uid} data-testid="players-table-row">
                  <TableCell>
                    <button
                      type="button"
                      className="player-link flex items-center gap-3 text-left"
                      data-testid={`player-row-name-${player.uid}`}
                      onClick={() => onOpenProfile(player.uid)}
                    >
                      <div className="avatar-fallback w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-xs font-semibold shrink-0">
                        {String(player.nick || player.name || player.uid).slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-1.5">
                          <strong className="text-sm truncate">{player.nick || player.name || "Sin alias"}</strong>
                          {showCulture && (
                            <Star
                              className="h-3 w-3 shrink-0 text-amber-500"
                              fill="currentColor"
                              data-testid={`player-show-culture-${player.uid}`}
                              title={`Show culture: ${showCulture.tier} (${(showCulture.rate * 100).toFixed(0)}% rate)`}
                            />
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">UID {player.uid}</span>
                      </div>
                    </button>
                  </TableCell>
                  <TableCell data-testid={`player-club-${player.uid}`}>
                    {player.membership?.club_name || player.membership?.club_id ? (
                      <button
                        type="button"
                        onClick={() =>
                          onSelectClub?.({
                            id: player.membership.club_id || null,
                            name: player.membership.club_name || null,
                          })
                        }
                        className="flex flex-col leading-tight text-left rounded px-1 -mx-1 hover:bg-cyan-50 transition-colors cursor-pointer"
                        data-testid={`player-club-btn-${player.uid}`}
                        title={`Filtrar por ${player.membership.club_name || `club ${player.membership.club_id}`}`}
                      >
                        <span className="text-sm truncate max-w-[170px] hover:text-cyan-700">
                          {player.membership.club_name || <span className="text-muted-foreground italic">sin nombre</span>}
                        </span>
                        {player.membership.club_id && (
                          <span className="text-[10px] text-muted-foreground font-mono tabular-nums">
                            ID {player.membership.club_id}
                          </span>
                        )}
                      </button>
                    ) : (
                      <span className="text-xs text-slate-300">—</span>
                    )}
                  </TableCell>
                  <TableCell data-testid={`player-union-${player.uid}`}>
                    {player.membership?.union_id ? (
                      <button
                        type="button"
                        onClick={() =>
                          onSelectUnion?.({
                            id: player.membership.union_id,
                            name: player.membership.union_name || null,
                          })
                        }
                        className="flex flex-col leading-tight text-left rounded px-1 -mx-1 hover:bg-cyan-50 transition-colors cursor-pointer"
                        data-testid={`player-union-btn-${player.uid}`}
                        title={`Filtrar por unión ${player.membership.union_id}`}
                      >
                        {player.membership.union_name && (
                          <span className="text-sm truncate max-w-[100px] hover:text-cyan-700" title={player.membership.union_name}>
                            {player.membership.union_name}
                          </span>
                        )}
                        <span className="text-[10px] text-muted-foreground font-mono tabular-nums hover:text-cyan-700">
                          ID {player.membership.union_id}
                        </span>
                      </button>
                    ) : (
                      <span className="text-xs text-slate-300">—</span>
                    )}
                  </TableCell>
                  <TableCell className="tabular-nums text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <SampleQualityBadge
                        quality={stats?.sample_quality}
                        hands={stats?.total_hands_profile}
                      />
                      <span>{formatNumber(player.hands_played ?? player.hands ?? 0)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <PlayerTypeBadge type={player.player_type} />
                  </TableCell>
                  {/* Cell vacía bajo el toggle column (mantener alineación) */}
                  <TableCell className="bg-slate-50/50" />
                  {statsExpanded && (
                    <>
                      <TableCell className="tabular-nums text-right text-sm">
                        {fmtPct(stats?.vpip)}
                      </TableCell>
                      <TableCell className="tabular-nums text-right text-sm">
                        {fmtPct(stats?.pfr)}
                      </TableCell>
                      <TableCell className="tabular-nums text-right text-sm">
                        {fmtPct(stats?.aggression_rate)}
                      </TableCell>
                      <TableCell className={`tabular-nums text-right text-sm ${profitColor(stats?.avg_profit_bb)}`}>
                        {stats?.avg_profit_bb != null
                          ? `${stats.avg_profit_bb > 0 ? "+" : ""}${Number(stats.avg_profit_bb).toFixed(2)}`
                          : "—"}
                      </TableCell>
                    </>
                  )}
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatEpochDate(player.last_seen_raw)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`player-row-open-${player.uid}`}
                      onClick={() => onOpenProfile(player.uid)}
                    >
                      Ver perfil
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function PlayersPage() {
  const [q, setQ] = useState("");
  const [profileUid, setProfileUid] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  // Expandido por default: muestra todas las stats. Click en el toggle column
  // (entre "Manos" y "VPIP") las colapsa para liberar espacio cuando agreguemos
  // más columnas en el futuro.
  const [statsExpanded, setStatsExpanded] = useState(true);
  // Filtros de afiliación: cuando el usuario clickea un Club o Unión en una fila,
  // guardamos {id, name} y filtramos localmente la tabla.
  const [clubFilter, setClubFilter] = useState(null);   // { id, name } | null
  const [unionFilter, setUnionFilter] = useState(null); // { id, name } | null
  // Filtro de actividad por rango (Argentina UTC-3). Server-side.
  const [dateFrom, setDateFrom] = useState(""); // "YYYY-MM-DD"
  const [dateTo, setDateTo] = useState("");

  const openProfileDrawer = (uid) => {
    setProfileUid(uid);
    setProfileOpen(true);
  };

  // Endpoint V2: /api/v2/users/search con filtros server-side.
  // Importante: club_id/union_id se aplican EN EL SERVIDOR, no localmente,
  // para no quedar limitados a los 100 jugadores más activos del lote actual.
  const query = useMemo(() => {
    const term = q.trim();
    const parts = [`q=${encodeURIComponent(term)}`, "limit=100"];
    if (dateFrom) parts.push(`active_from=${dateFrom}`);
    if (dateTo) parts.push(`active_to=${dateTo}`);
    if (clubFilter?.id) parts.push(`club_id=${encodeURIComponent(clubFilter.id)}`);
    if (unionFilter?.id) parts.push(`union_id=${encodeURIComponent(unionFilter.id)}`);
    return `/v2/users/search?${parts.join("&")}`;
  }, [q, dateFrom, dateTo, clubFilter, unionFilter]);

  // Query secundaria SOLO para poblar los dropdowns de Club/Unión: trae el
  // top-100 SIN aplicar el filtro de club/union activo, así el usuario puede
  // cambiar de filtro sin perder la lista de opciones. Sí respeta el rango de
  // fechas porque el universo de clubs/uniones dependende de la ventana
  // temporal de actividad.
  const optionsQuery = useMemo(() => {
    const parts = ["q=", "limit=100"];
    if (dateFrom) parts.push(`active_from=${dateFrom}`);
    if (dateTo) parts.push(`active_to=${dateTo}`);
    return `/v2/users/search?${parts.join("&")}`;
  }, [dateFrom, dateTo]);

  const { data, loading, error, refetch } = useApi(query);
  const { data: optionsData } = useApi(optionsQuery);
  const playersRaw = data?.items || [];
  const total = data?.count ?? playersRaw.length;
  const optionsItems = optionsData?.items || playersRaw;

  // Opciones únicas de Club y Unión presentes en `optionsItems` (request
  // independiente del filtro activo). Ordenadas por count desc.
  const { clubOptions, unionOptions } = useMemo(() => {
    const clubMap = new Map(); // id -> { id, name, count }
    const unionMap = new Map();
    for (const p of optionsItems) {
      const m = p.membership;
      if (!m) continue;
      if (m.club_id) {
        const key = String(m.club_id);
        const prev = clubMap.get(key);
        clubMap.set(key, {
          id: key,
          name: m.club_name || prev?.name || null,
          count: (prev?.count || 0) + 1,
        });
      }
      if (m.union_id) {
        const key = String(m.union_id);
        const prev = unionMap.get(key);
        unionMap.set(key, {
          id: key,
          name: m.union_name || prev?.name || null,
          count: (prev?.count || 0) + 1,
        });
      }
    }
    const byCountDesc = (a, b) => b.count - a.count || String(a.name || a.id).localeCompare(String(b.name || b.id));
    return {
      clubOptions: Array.from(clubMap.values()).sort(byCountDesc),
      unionOptions: Array.from(unionMap.values()).sort(byCountDesc),
    };
  }, [optionsItems]);

  // Server-side ya filtra por club/union. No volvemos a filtrar local.
  const players = playersRaw;

  return (
    <>
      <PageHeader
        eyebrow="Perfiles"
        title="Jugadores"
        description="Listado con stats reales (VPIP / PFR / Agresión / Profit BB) calculados desde Collector V2."
      />
      <Card className="filter-card">
        <CardContent className="filter-content flex flex-wrap items-center gap-3 pt-6">
          <div className="filter-input flex items-center gap-2 flex-1 min-w-[220px] border rounded-md px-3 py-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="players-search-input"
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Buscar por UID o nick..."
              className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0"
            />
          </div>
          <div className="flex items-center gap-1.5 border rounded-md px-2.5 py-2 bg-gray-50">
            <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider mr-1">
              Activos
            </span>
            <span className="text-[11px] text-muted-foreground">desde</span>
            <input
              data-testid="players-date-from-input"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              max={dateTo || new Date().toISOString().slice(0, 10)}
              className="bg-transparent border-0 outline-none text-sm tabular-nums"
            />
            <span className="text-[11px] text-muted-foreground">hasta</span>
            <input
              data-testid="players-date-to-input"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              min={dateFrom || undefined}
              max={new Date().toISOString().slice(0, 10)}
              className="bg-transparent border-0 outline-none text-sm tabular-nums"
            />
            {(dateFrom || dateTo) && (
              <button
                type="button"
                onClick={() => {
                  setDateFrom("");
                  setDateTo("");
                }}
                className="ml-1 rounded-full hover:bg-rose-100 text-rose-600 p-0.5"
                data-testid="players-date-clear"
                aria-label="Quitar rango de fechas"
                title="Quitar rango de fechas"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Button
            data-testid="players-refresh-button"
            variant="outline"
            onClick={refetch}
          >
            Refrescar
          </Button>
        </CardContent>
      </Card>

      {(clubFilter || unionFilter || dateFrom || dateTo) && (
        <div
          className="flex flex-wrap items-center gap-2 mb-3"
          data-testid="players-active-filters"
        >
          <span className="text-xs uppercase tracking-wider text-muted-foreground">
            Filtros activos:
          </span>
          {(dateFrom || dateTo) && (
            <Badge
              variant="outline"
              className="bg-amber-50 border-amber-300 text-amber-800 gap-1.5 pl-2 pr-1 py-1"
              data-testid="active-filter-date"
            >
              <CalendarDays className="h-3 w-3" />
              <span className="text-[11px] font-semibold">
                {dateFrom && dateTo
                  ? "Activos entre:"
                  : dateFrom
                  ? "Activos desde:"
                  : "Activos hasta:"}
              </span>
              <span className="text-[11px] tabular-nums">
                {dateFrom && dateTo
                  ? `${dateFrom} → ${dateTo}`
                  : dateFrom || dateTo}
              </span>
              <button
                type="button"
                onClick={() => {
                  setDateFrom("");
                  setDateTo("");
                }}
                className="rounded-full hover:bg-amber-200/60 p-0.5 transition-colors"
                data-testid="active-filter-date-clear"
                aria-label="Quitar rango de fechas"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {clubFilter && (
            <Badge
              variant="outline"
              className="bg-cyan-50 border-cyan-300 text-cyan-800 gap-1.5 pl-2 pr-1 py-1"
              data-testid="active-filter-club"
            >
              <span className="text-[11px] font-semibold">Club:</span>
              <span className="text-[11px]">
                {clubFilter.name || `ID ${clubFilter.id}`}
                {clubFilter.name && clubFilter.id ? (
                  <span className="ml-1 text-cyan-600/70 font-mono">({clubFilter.id})</span>
                ) : null}
              </span>
              <button
                type="button"
                onClick={() => setClubFilter(null)}
                className="rounded-full hover:bg-cyan-200/60 p-0.5 transition-colors"
                data-testid="active-filter-club-clear"
                aria-label="Quitar filtro de club"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {unionFilter && (
            <Badge
              variant="outline"
              className="bg-violet-50 border-violet-300 text-violet-800 gap-1.5 pl-2 pr-1 py-1"
              data-testid="active-filter-union"
            >
              <span className="text-[11px] font-semibold">Unión:</span>
              <span className="text-[11px]">
                {unionFilter.name || `ID ${unionFilter.id}`}
              </span>
              <button
                type="button"
                onClick={() => setUnionFilter(null)}
                className="rounded-full hover:bg-violet-200/60 p-0.5 transition-colors"
                data-testid="active-filter-union-clear"
                aria-label="Quitar filtro de unión"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          <button
            type="button"
            onClick={() => {
              setClubFilter(null);
              setUnionFilter(null);
              setDateFrom("");
              setDateTo("");
            }}
            className="text-[11px] text-muted-foreground hover:text-rose-600 underline-offset-2 hover:underline"
            data-testid="active-filters-clear-all"
          >
            limpiar todo
          </button>
        </div>
      )}

      {loading && <LoadingState />}
      {error && <ErrorState message={error} onRetry={refetch} />}
      {!loading && !error && players.length === 0 && (
        <EmptyState
          title={
            clubFilter || unionFilter
              ? "Sin jugadores en ese club/unión"
              : dateFrom || dateTo
              ? "Sin jugadores activos en el rango"
              : q.trim()
              ? "Sin resultados"
              : "Sin jugadores capturados"
          }
          description={
            clubFilter || unionFilter
              ? "Ningún jugador registrado coincide con el club/unión y el rango de fechas seleccionado."
              : dateFrom || dateTo
              ? `No se registraron manos en ese rango. Probá con otras fechas o quitá el filtro.`
              : q.trim()
              ? `No se encontraron jugadores que coincidan con "${q}".`
              : "Cuando Collector V2 registre jugadores, aparecerán aquí automáticamente."
          }
          icon={UserRound}
          testid="players-empty-state"
          cta={false}
        />
      )}
      {!loading && !error && players.length > 0 && (
        <>
          <p className="text-xs text-muted-foreground mb-2" data-testid="players-result-count">
            Mostrando {players.length} {players.length === 1 ? "jugador" : "jugadores"}
            {total > players.length ? ` de ${total} encontrados` : ""}
            <span className="ml-2 text-slate-400">·</span>
            <span className="ml-2">⭐ exhibicionista</span>
            <span className="ml-2 text-slate-400">·</span>
            <span className="ml-2 text-emerald-600 font-bold">★★★★</span> sólido
            <span className="ml-2 text-emerald-500 font-bold">★★★</span> confiable
            <span className="ml-2 text-amber-500 font-bold">★★</span> orientativo
            <span className="ml-2 text-slate-300 font-bold">★</span> insuficiente
          </p>
          <PlayersTable
            players={players}
            onOpenProfile={openProfileDrawer}
            statsExpanded={statsExpanded}
            onToggleStats={() => setStatsExpanded((v) => !v)}
            onSelectClub={setClubFilter}
            onSelectUnion={setUnionFilter}
            clubOptions={clubOptions}
            unionOptions={unionOptions}
            clubFilter={clubFilter}
            unionFilter={unionFilter}
          />
        </>
      )}
      <PlayerProfileDrawer
        uid={profileUid}
        open={profileOpen}
        onOpenChange={setProfileOpen}
      />
    </>
  );
}
